import { MappedHttpErrorResponse } from '@adnova/jf-ng-components';
import { ProblemDTO } from '../../../openapi/shell-service-openapi';

/** Gemeinsame Fehlermeldungen nach HTTP-Status-Code (für alle Operationen). */
const COMMON_ERROR_MESSAGES: Record<number, string> = {
  0: 'Keine Verbindung zum Server. Bitte prüfe deine Internetverbindung und versuche es erneut.',
  403: 'Fehlende Berechtigung. Bitte kontaktiere den Just Farming Benutzerservice.',
  413: 'Das Bild ist zu groß. Maximal 1 MB erlaubt.',
  415: 'Dieser Dateityp wird nicht unterstützt.',
  504: 'Der Server hat nicht rechtzeitig geantwortet. Bitte probiere es später erneut.',
};

/** Operationsspezifische Fehlermeldungen (Default-Texte pro Operation). */
export const ERROR_MESSAGES = {
  save: {
    ...COMMON_ERROR_MESSAGES,
    400: 'Ungültige Eingaben. Bitte prüfe die Formularfelder.',
    404: 'Das Pop-up wurde nicht gefunden. Möglicherweise wurde es gelöscht.',
    default: 'Fehler beim Speichern. Bitte probiere es später erneut.',
  },
  publish: {
    ...COMMON_ERROR_MESSAGES,
    400: 'Ungültige Eingaben. Bitte prüfe die Formularfelder.',
    404: 'Das Pop-up wurde nicht gefunden. Möglicherweise wurde es gelöscht.',
    default: 'Fehler beim Veröffentlichen. Bitte probiere es später erneut.',
  },
  unpublish: {
    ...COMMON_ERROR_MESSAGES,
    400: 'Ungültige Eingaben. Bitte prüfe die Formularfelder.',
    404: 'Das Pop-up wurde nicht gefunden. Möglicherweise wurde es gelöscht.',
    default: 'Fehler beim Zurückziehen. Bitte probiere es später erneut.',
  },
  delete: {
    ...COMMON_ERROR_MESSAGES,
    400: 'Ungültige Eingaben. Bitte prüfe die Formularfelder.',
    404: 'Das Pop-up wurde nicht gefunden. Möglicherweise wurde es bereits gelöscht.',
    default: 'Fehler beim Löschen. Bitte probiere es später erneut.',
  },
  loadList: {
    ...COMMON_ERROR_MESSAGES,
    default: 'Fehler beim Laden der Announcements. Bitte probiere es später erneut.',
  },
  loadStatistics: {
    ...COMMON_ERROR_MESSAGES,
    default: 'Fehler beim Laden der Statistiken. Bitte probiere es später erneut.',
  },
  loadDetail: {
    ...COMMON_ERROR_MESSAGES,
    404: 'Das Pop-up wurde nicht gefunden.',
    default: 'Fehler beim Laden der Details. Bitte probiere es später erneut.',
  },
  preview: {
    ...COMMON_ERROR_MESSAGES,
    404: 'Das Pop-up wurde nicht gefunden.',
    default: 'Fehler beim Laden der Vorschau. Bitte probiere es später erneut.',
  },
} as const;

/**
 * Ermittelt die passende Fehlermeldung anhand von Status-Code und Operation.
 *
 * Priorität:
 * 1. Backend-Fehlertext (ProblemDTO.error), falls vorhanden und nicht nur HTTP-Statustext
 * 2. Frontend-Fallback nach Status-Code und Operation
 *
 * Hinweis: Das Backend liefert aktuell keine benutzerfreundlichen Fehlermeldungen
 * (server.error.include-message=never). Falls das Backend zukünftig auf JSON Problem
 * (RFC 7807) umgestellt wird und benutzerfreundliche Texte liefert (z.B. über ein
 * `message`- oder `detail`-Feld), kann diese Funktion entsprechend erweitert werden,
 * um Backend-Meldungen zu bevorzugen.
 */
export function getErrorMessage(
  error: MappedHttpErrorResponse<ProblemDTO>,
  operation: keyof typeof ERROR_MESSAGES,
): string {
  // Prüfe, ob das Backend einen spezifischen Fehlertext liefert,
  // der über den reinen HTTP-Statustext hinausgeht.
  const backendMessage = error.error?.error;
  const httpStatusText = error.statusText;
  const hasSpecificBackendMessage = backendMessage
    && backendMessage !== httpStatusText
    && backendMessage !== 'Bad Request'
    && backendMessage !== 'Not Found'
    && backendMessage !== 'Internal Server Error';

  if (hasSpecificBackendMessage) {
    return backendMessage;
  }

  // Frontend-Fallback: Status-Code-basierte Meldung
  const messages: Record<string, string> = ERROR_MESSAGES[operation];
  const status = error.status;
  return (status !== undefined && status in messages)
    ? messages[status]
    : messages['default'];
}
